import { AG_GRID_LOCALE_FR } from '@ag-grid-community/locale';
import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import {
  BarsLoaderComponent,
  LittleBarsLoaderComponent,
  ActCellNameRendererComponent,
  formatDate,
  ActCellIconType,
  UiStateService,
} from '@upassignations/core';
import { AgGridModule } from 'ag-grid-angular';
import {
  CellStyleModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  ColDef,
  CustomFilterModule,
  DateFilterModule,
  GridOptions,
  GridReadyEvent,
  InfiniteRowModelModule,
  NumberFilterModule,
  PaginationModule,
  RowSelectionModule,
  RowStyleModule,
  TextFilterModule,
  themeQuartz,
  ValidationModule,
  RowSelectionOptions,
  RowNode,
  iconOverrides,
} from 'ag-grid-community';
import { CellSelectionModule, SetFilterModule } from 'ag-grid-enterprise';
import { BehaviorSubject, interval, map, Observable } from 'rxjs';
import { FeatureService } from '../../services/feature.service';
import { SidePanel } from '../../models/current-panel';
import { MetadataPanelComponent } from '../metadata-panel/metadata-panel.component';

@Component({
  selector: 'lib-logs-table',
  imports: [
    AgGridModule,
    CommonModule,
    LittleBarsLoaderComponent,
    MetadataPanelComponent,
  ],
  templateUrl: './logs-table.component.html',
  styleUrl: './logs-table.component.scss',
})
export class LogsTableComponent {
  private logSubject = new BehaviorSubject<any[]>([]);
  rowData$ = this.logSubject.asObservable();
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  isLoading$ = this.isLoadingSubject.asObservable();
  private metadataSubject = new BehaviorSubject<any[]>([]);
  metadata$ = this.metadataSubject.asObservable();

  gridApi: any;
  currentPage = 0;
  currentPageSize = 250;
  openSidePanel = false;
  currentSidePanel = SidePanel.Metadata;
  currentMetadata: any = null;
  selectedNodes: RowNode[] = [];
  loadingMetadata = false;

  SidePanel = SidePanel;

  localeText: Record<string, string> = AG_GRID_LOCALE_FR;

  modules = [
    ClientSideRowModelModule,
    ValidationModule,
    CellStyleModule,
    PaginationModule,
    TextFilterModule,
    NumberFilterModule,
    DateFilterModule,
    CustomFilterModule,
    SetFilterModule,
    CellSelectionModule,
    RowStyleModule,
    ClientSideRowModelApiModule,
    RowSelectionModule,
    InfiniteRowModelModule,
  ];

  theme = themeQuartz
    .withParams({
      fontSize: 12,
      oddRowBackgroundColor: '#f8f8f8',
      rangeSelectionBorderColor: '#ff7900',
      rowHoverColor: '#ff790025',
      selectedRowBackgroundColor: '#ff790025',
      accentColor: '#ff7900',
    })
    .withPart(
      iconOverrides({
        type: 'image',
        mask: true,
        icons: {
          filter: {
            url: 'assets/icons/filter-icon.svg',
          },
          'menu-alt': {
            url: 'assets/icons/horizontal-bar-menu.svg',
          },
        },
      })
    );
  rowSelection: RowSelectionOptions | 'single' | 'multiple' = {
    mode: 'singleRow',
    checkboxes: false,
    enableClickSelection: true,
  };

  gridOptions: GridOptions = {
    pagination: true,
    paginationAutoPageSize: false,
    paginationPageSize: 250,
    paginationPageSizeSelector: [250, 500, 1000, 2500],
    loadingOverlayComponent: BarsLoaderComponent,
    suppressContextMenu: true,
    onPaginationChanged: this.onPaginationChanged.bind(this),
  };

  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    flex: 1,
    wrapText: true,
    autoHeight: true,
    resizable: true,
    wrapHeaderText: true,
    cellStyle: {
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
      lineHeight: '1.2',
      paddingTop: '2px',
      paddingBottom: '2px',
    },
    headerClass: 'ag-header',
  };

  columnDefs: ColDef[] = [
    {
      headerName: 'Service',
      field: 'service',
      minWidth: 450,
      cellRenderer: ActCellNameRendererComponent,
      cellRendererParams: this.getInnerRendererParams(),
    },
    {
      headerName: 'Date',
      field: 'date',
      filter: 'agDateColumnFilter',
      minWidth: 108,
      width: 108,
      maxWidth: 140
    },
    {
      headerName: 'Heure',
      field: 'time',
      filter: 'agDateColumnFilter',
      minWidth: 106,
      width: 106,
      maxWidth: 140
    },
    {
      headerName: 'Utilisateur',
      field: 'userName',
      minWidth: 129,
      width: 129,
      maxWidth: 200
    },
    {
      headerName: 'Message',
      field: 'message',
      minWidth: 800,
      width: 800,
      maxWidth: 900
    },
    {
      headerName: 'Erreur',
      field: 'failure',
      minWidth: 31,
      width: 31,
      cellRenderer: (params) => this.failureCellRender(params.value),
      cellStyle: {
        display: 'flex',
        'justify-content': 'center',
        'align-items': 'center',
      },
    },
  ];

  constructor(
    // eslint-disable-next-line no-unused-vars
    private featureService: FeatureService,
    // eslint-disable-next-line no-unused-vars
    private uiStateService: UiStateService
  ) {}

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.rowData$.subscribe((data) => {
      params.api.setGridOption('loading', !data?.length);
    });
    this.refreshLogs(false);

    interval(600000).subscribe(() => {
      this.saveSelectedColumnId();
      this.saveScrollPosition();
      this.refreshLogs(true);
    });
  }
  saveScrollPosition(): void {
    if (this.gridApi) {
      const scrollTop = this.gridApi.getVerticalPixelRange().top;
      this.uiStateService.setScrollPosition(scrollTop);
    }
  }
  saveSelectedColumnId(): void {
    if (!this.gridApi) return;

    const focusedCell = this.gridApi.getFocusedCell();

    if (focusedCell) {
      const columnId = focusedCell.column.getColId();
      this.uiStateService.setSelectedColumnId(columnId);
    }
  }

  restoreSelection(): void {
    setTimeout(() => {
      const selectedRowId = this.uiStateService.getSelectedRowId();
      const savedScrollPosition = this.uiStateService.getScrollPosition();
      const selectedCellColumnId = this.uiStateService.getSelectedColumnId();

      if (!this.gridApi) return;

      if (savedScrollPosition != null) {
        const rowHeight = this.gridApi.getSizesForCurrentTheme().rowHeight;
        const targetIndex = Math.floor(savedScrollPosition / rowHeight);
        this.gridApi.ensureIndexVisible(targetIndex, 'top');
      }

      if (selectedRowId) {
        const uri = selectedRowId.replace('row_', '');

        this.gridApi.forEachNode((node: RowNode) => {
          if (node.data?.uri === uri) {
            node.setSelected(true);

            if (selectedCellColumnId) {
              this.gridApi.setFocusedCell(node.rowIndex, selectedCellColumnId);
            }
          }
        });
      }
    }, 0);
  }

  getInnerRendererParams(): any {
    return {
      showMetadata: (uri: string, params: any) =>
        this.showMetadata(uri, params),
      closeSidePanel: () => this.closeSidePanel(),
      manuallySelectRow: (node: RowNode) => this.manuallySelectRow(node),
      downloadLitigationDocument: (litigationUri) =>
        this.downloadLitigationDocument(litigationUri),
      displayingInLogs: true,
    };
  }

  refreshLogs(showLoader: boolean): void {
    if (showLoader) {
      this.isLoadingSubject.next(true);
    }

    this.getParsedLogs$().subscribe({
      next: (response) => {
        const fakeLogs = Array.from(
          { length: response.totalNumberOfLogs },
          (_, i) =>
            response.logs[i - this.currentPage * this.currentPageSize] || {},
          console.log(response)
        );

        this.logSubject.next(fakeLogs);
        this.isLoadingSubject.next(false);
        this.restoreSelection();
      },
      error: () => this.isLoadingSubject.next(false),
    });
  }

  getParsedLogs$(): Observable<{ logs: any[]; totalNumberOfLogs: number }> {
    return this.featureService
      .getListOfLogs$(this.currentPage + 1, this.currentPageSize)
      .pipe(
        map((response: any) => {
          const parsedLogs = response.logs.map((log) => {
            const { date, time } = this.parseTimestamp(log.date);
            return {
              ...log,
              date,
              time,
            };
          });

          return {
            logs: parsedLogs,
            totalNumberOfLogs: response.totalNumberOfLogs,
          };
        })
      );
  }
  failureCellRender(error: boolean): string {
    if (!error) {
      return '';
    } else {
      return this.warningIcon();
    }
  }
  warningIcon(): string {
    return `<img 
      src='assets/icons/warning.svg' 
      alt='Erreur'
      style="width: 25px; height:25px;"/>`;
  }

  onPaginationChanged(): void {
    if (!this.gridApi) return;

    const newPage = this.gridApi.paginationGetCurrentPage();
    const newPageSize = this.gridApi.paginationGetPageSize();

    this.handlePaginationChange(newPage, newPageSize);
  }
  handlePaginationChange(newPage: number, newPageSize: number): void {
    const hasPageChanged =
      newPage !== this.currentPage || newPageSize !== this.currentPageSize;

    if (hasPageChanged) {
      this.currentPage = newPage;
      this.currentPageSize = newPageSize;
      this.refreshLogs(true);
    }
  }

  parseTimestamp(timestamp: string): { date: string; time: string } {
    if (!timestamp) return { date: '', time: '' };

    const [datePart, timePart] = timestamp.split('T');
    const time = timePart.split('.')[0];

    const date = new Date(datePart);
    const formattedDate = `${date.getDate().toString().padStart(2, '0')}/${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, '0')}/${date.getFullYear()}`;

    const frenchTime = new Date(`${datePart}T${time}Z`);
    const frenchTimeFormat = frenchTime.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

    return { date: formattedDate, time: frenchTimeFormat };
  }

  resetFilters(): void {
    if (this.gridApi) {
      this.gridApi.setFilterModel(null);
    }
  }

  handleExcelExport(): void {
    if (this.gridApi) {
      const urisToExport = this.getURIsToExport();
      this.exportToExcel(urisToExport);
    }
  }

  getURIsToExport(): string[] {
    const filteredUris: string[] = [];
    this.gridApi.forEachNodeAfterFilter((node) => {
      if (node.data?.uri) {
        filteredUris.push(node.data.uri);
      }
    });
    return filteredUris;
  }

  exportToExcel(urisToExport: string[]): void {
    this.featureService.exportLogs$(urisToExport).subscribe({
      next: (response) => {
        if (!response.error) {
          this.downloadGeneratedFile(response.fileName);
        }
      },
    });
  }
  downloadLitigationDocument(litigationUri: string): void {
    this.featureService.downloadLitigationDocument$(litigationUri).subscribe({
      next: (response) => {
        if (!response.error) {
          this.downloadGeneratedFile(response.fileName);
        }
      },
    });
  }

  downloadGeneratedFile(fileName: string): void {
    if (fileName) {
      this.featureService
        .downloadExportedFileFromServer(fileName)
        .subscribe((response) => {
          this.convertFileStreamToLocalFile(response, fileName);
        });
    }
  }

  convertFileStreamToLocalFile(fileStream: BlobPart, fileName: string): void {
    const blob = new Blob([fileStream], { type: 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
  }

  subscribeToMetadata$(uri: string): any {
    this.openSidePanel = true;
    this.featureService.getLogMetadata$(uri).subscribe({
      next: (metadatas) => {
        const formattedMetadata = metadatas.map(({ key, value }) => ({
          key,
          value: this.formatMetadataValue(key, value),
        }));

        this.metadataSubject.next(formattedMetadata);
        this.currentMetadata = formattedMetadata;
        this.updateSidePanelLoadingStatus(SidePanel.Metadata, false);
      },
    });
  }

  updateSidePanelLoadingStatus(sidePanel: SidePanel, status: boolean): void {
    if (sidePanel === SidePanel.Metadata) {
      this.loadingMetadata = status;
    }
  }

  formatMetadataValue(key: string, value: any): any {
    if (key.toLowerCase().includes('date') && typeof value === 'string') {
      return formatDate(value);
    }
    if (value === true) return 'oui';
    if (value === false) return 'non';
    if (value === null) return '';
    return value;
  }

  showMetadata(uri: string, params: any): void {
    this.updateCurrentSidePanel(SidePanel.Metadata);
    this.updateSidePanelLoadingStatus(SidePanel.Metadata, true);
    this.subscribeToMetadata$(uri);

    if (!params.node.isSelected()) {
      this.manuallySelectRow(params.node);
    }
  }

  manuallySelectRow(node: RowNode): void {
    this.selectedNodes = [node];
    node.setSelected(true);
  }

  updateCurrentSidePanel(sidePanel: SidePanel): void {
    this.currentSidePanel = sidePanel;
  }

  closeSidePanel(): void {
    this.uiStateService.clearActiveIcons();
    this.openSidePanel = false;
    setTimeout(() => {
      this.currentMetadata = null;
    }, 1200);
  }

  onRowClicked(event: any): void {
    const selectedNode: RowNode = event.node;
    this.selectedNodes = [selectedNode];
    const uri = selectedNode.data.uri;
    const rowId = `row_${uri}`;
    this.uiStateService.setSelectedRowId(rowId);

    if (!this.openSidePanel) {
      return;
    }

    const cellId = `cell_${uri}`;

    if (this.currentSidePanel === SidePanel.Metadata) {
      if (this.uiStateService.shouldDisplayMetadata(uri)) {
        this.updateSidePanelLoadingStatus(SidePanel.Metadata, true);
        this.subscribeToMetadata$(uri);
        this.uiStateService.setLastDisplayedMetadataUri(uri);
      }
      this.uiStateService.setActiveIcon(cellId, ActCellIconType.Info);
    }
  }
}
